function renderColumnDefTemplate({
  sortLabelExpr,
  sortableHeaderButtonJsx
}: {
  sortLabelExpr: string
  sortableHeaderButtonJsx: string
}): string {
  return `header: ({ column }) => {
      ${sortLabelExpr}
      return (
        ${sortableHeaderButtonJsx}
      )
    }`
}

function renderColumnDefLayoutTemplate({
  generateAuthorshipValue
}: {
  generateAuthorshipValue: string
}): string {
  return `cell: ({ row }) => {
      ${generateAuthorshipValue}
      return label ? (
        <div className="text-sm font-medium" title={actor?.email}>
          {label}
        </div>
      ) : (
        <div className="text-muted-foreground">Unavailable</div>
      )
    }`
}

function renderColumnDefContentTemplate({
  generateImageCellMedia
}: {
  generateImageCellMedia: string
}): string {
  return `cell: ({ row }) => {
      ${generateImageCellMedia}
    }`
}

function renderColumnDefRenderGenerateSelectLabelTemplate({
  columnAccessorKey,
  generateSelectLabel
}: {
  columnAccessorKey: string
  generateSelectLabel: string
}): string {
  return `cell: ({ row }) => {
      const value = row.getValue('${columnAccessorKey}')
      ${generateSelectLabel}
      return (
        <Badge variant="outline">
          {label || 'Unavailable'}
        </Badge>
      )
    }`
}

function renderColumnDefRenderGenerateRelationshipLabelTemplate({
  columnAccessorKey,
  generateRelationshipLabel
}: {
  columnAccessorKey: string
  generateRelationshipLabel: string
}): string {
  return `cell: ({ row }) => {
      const value = row.getValue('${columnAccessorKey}')
      ${generateRelationshipLabel}
      return (
        <Badge variant="outline">
          {label || 'Unavailable'}
        </Badge>
      )
    }`
}

function renderColumnDefRenderDetailsTemplate({
  columnAccessorKey
}: {
  columnAccessorKey: string
}): string {
  return `cell: ({ row }) => {
      const value = row.getValue('${columnAccessorKey}')
      return (
        <Badge variant="outline">
          {value === null || value === undefined ? 'Unavailable' : String(value)}
        </Badge>
      )
    }`
}

function renderColumnDefLayoutDetailsTemplate({
  columnAccessorKey
}: {
  columnAccessorKey: string
}): string {
  return `cell: ({ row }) => {
      const date = new Date(row.getValue('${columnAccessorKey}'))
      return (
        <div className="text-sm">
          {date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })}
        </div>
      )
    }`
}

function renderColumnDefLayoutDetailsBranchTemplate({
  columnAccessorKey
}: {
  columnAccessorKey: string
}): string {
  return `cell: ({ row }) => {
      const email = row.getValue('${columnAccessorKey}') as string
      return <div className="text-sm font-medium">{email}</div>
    }`
}

function renderColumnDefLayoutDetailsBranchBodyTemplate({
  columnAccessorKey
}: {
  columnAccessorKey: string
}): string {
  return `cell: ({ row }) => {
      const amount = row.getValue('${columnAccessorKey}') as number
      return (
        <div className="text-sm font-medium">
          {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)}
        </div>
      )
    }`
}

function renderNumberColumnCell({ columnAccessorKey }: { columnAccessorKey: string }): string {
  return `cell: ({ row }) => {
      const value = row.getValue('${columnAccessorKey}') as number
      return <div className="text-sm">{value}</div>
    }`
}

function renderColumnDefRenderDetailsBranchTemplate({
  columnAccessorKey,
  trueLabel,
  falseLabel
}: {
  columnAccessorKey: string
  trueLabel: string
  falseLabel: string
}): string {
  return `cell: ({ row }) => {
      const value = row.getValue('${columnAccessorKey}') as boolean
      return (
        <Badge variant={value ? 'outline' : 'secondary'}>
          {value ? ${trueLabel} : ${falseLabel}}
        </Badge>
      )
    }`
}

function renderLinkColumnCell({ columnAccessorKey }: { columnAccessorKey: string }): string {
  return `cell: ({ row }) => {
      const value = row.getValue('${columnAccessorKey}') as string
      if (!value) return <div className="text-muted-foreground">\u2014</div>
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
          title={value}
        >
          {truncateText(value, 50)}
        </a>
      )
    }`
}

function renderColumnDefContentDetailsTemplate({
  columnComponent
}: {
  columnComponent: string
}): string {
  return `cell: ({ row }) => {
      return <${columnComponent} data={row.original} />
    }`
}

function renderColumnDefValueTemplate({
  columnAccessorKey,
  generateSelectLabel
}: {
  columnAccessorKey: string
  generateSelectLabel: string
}): string {
  return `cell: ({ row }) => {
      const value = row.getValue('${columnAccessorKey}')
      ${generateSelectLabel}
      return <div>{label || 'Unavailable'}</div>
    }`
}

function renderColumnDefValueGenerateRelationshipLabelTemplate({
  columnAccessorKey,
  generateRelationshipLabel
}: {
  columnAccessorKey: string
  generateRelationshipLabel: string
}): string {
  return `cell: ({ row }) => {
      const value = row.getValue('${columnAccessorKey}')
      ${generateRelationshipLabel}
      return <div>{label || 'Unavailable'}</div>
    }`
}

function renderColumnDefValueDetailsTemplate({
  columnAccessorKey
}: {
  columnAccessorKey: string
}): string {
  return `cell: ({ row }) => {
      const value = row.getValue('${columnAccessorKey}')
      return <div>{value === null || value === undefined ? 'Unavailable' : String(value)}</div>
    }`
}

function renderColumnDefContentBuildColumnSizeTemplate({
  columnAccessorKey,
  columnSize,
  meta,
  headerDef,
  cellDef
}: {
  columnAccessorKey: string
  columnSize: string
  meta: string
  headerDef: string
  cellDef: string
}): string {
  return `  {
    accessorKey: '${columnAccessorKey}',
    ${columnSize}${meta}${headerDef},
    ${cellDef}
  }`
}

export function renderColumnDef({
  columnAccessorKey,
  columnSize,
  meta,
  header,
  cell
}: {
  columnAccessorKey: string
  columnSize: string
  meta: string
  header:
    | { sortable: true; sortLabelExpr: string; sortableHeaderButtonJsx: string }
    | { sortable: false; columnHeader: string }
  cell:
    | { kind: 'authorship'; generateAuthorshipValue: string }
    | { kind: 'image'; generateImageCellMedia: string }
    | { kind: 'select-badge'; columnAccessorKey: string; generateSelectLabel: string }
    | { kind: 'relationship-badge'; columnAccessorKey: string; generateRelationshipLabel: string }
    | { kind: 'badge-value'; columnAccessorKey: string }
    | { kind: 'date'; columnAccessorKey: string }
    | { kind: 'email'; columnAccessorKey: string }
    | { kind: 'currency'; columnAccessorKey: string }
    | { kind: 'number'; columnAccessorKey: string }
    | { kind: 'boolean'; columnAccessorKey: string; trueLabel: string; falseLabel: string }
    | { kind: 'link'; columnAccessorKey: string }
    | { kind: 'custom'; columnComponent: string }
    | { kind: 'select'; columnAccessorKey: string; generateSelectLabel: string }
    | { kind: 'relationship'; columnAccessorKey: string; generateRelationshipLabel: string }
    | { kind: 'plain'; columnAccessorKey: string }
}): string {
  const headerDef = header.sortable
    ? renderColumnDefTemplate(header)
    : `header: ${header.columnHeader}`

  let cellDef: string
  switch (cell.kind) {
    case 'authorship':
      cellDef = renderColumnDefLayoutTemplate(cell)
      break
    case 'image':
      cellDef = renderColumnDefContentTemplate(cell)
      break
    case 'select-badge':
      cellDef = renderColumnDefRenderGenerateSelectLabelTemplate(cell)
      break
    case 'relationship-badge':
      cellDef = renderColumnDefRenderGenerateRelationshipLabelTemplate(cell)
      break
    case 'badge-value':
      cellDef = renderColumnDefRenderDetailsTemplate(cell)
      break
    case 'date':
      cellDef = renderColumnDefLayoutDetailsTemplate(cell)
      break
    case 'email':
      cellDef = renderColumnDefLayoutDetailsBranchTemplate(cell)
      break
    case 'currency':
      cellDef = renderColumnDefLayoutDetailsBranchBodyTemplate(cell)
      break
    case 'number':
      cellDef = renderNumberColumnCell(cell)
      break
    case 'boolean':
      cellDef = renderColumnDefRenderDetailsBranchTemplate(cell)
      break
    case 'link':
      cellDef = renderLinkColumnCell(cell)
      break
    case 'custom':
      cellDef = renderColumnDefContentDetailsTemplate(cell)
      break
    case 'select':
      cellDef = renderColumnDefValueTemplate(cell)
      break
    case 'relationship':
      cellDef = renderColumnDefValueGenerateRelationshipLabelTemplate(cell)
      break
    case 'plain':
      cellDef = renderColumnDefValueDetailsTemplate(cell)
      break
  }

  return renderColumnDefContentBuildColumnSizeTemplate({
    columnAccessorKey,
    columnSize,
    meta,
    headerDef,
    cellDef
  })
}
